import { useState, useMemo } from "react";
import { Search, MapPin, Package, ChevronDown } from "lucide-react";
import { useParcelLocations, getCourierLabel, type ParcelLocation } from "@/hooks/useParcelLocations";

interface ParcelLockerSelectorProps {
  onSelect: (location: ParcelLocation | null) => void;
  selected: ParcelLocation | null;
  courierFilter?: string;
}

const ParcelLockerSelector = ({ onSelect, selected, courierFilter: externalCourierFilter }: ParcelLockerSelectorProps) => {
  const { locations, couriers, isLoading, error } = useParcelLocations("LT");
  const [search, setSearch] = useState("");
  const [internalCourierFilter, setInternalCourierFilter] = useState<string>("all");
  const activeCourier = externalCourierFilter || internalCourierFilter;
  const [showDropdown, setShowDropdown] = useState(!selected);

  const filtered = useMemo(() => {
    let result = locations;
    if (activeCourier !== "all") {
      result = result.filter(l => l.courier_code === activeCourier);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        l.name.toLowerCase().includes(q) ||
        l.address.toLowerCase().includes(q) ||
        l.city.toLowerCase().includes(q) ||
        l.postal_code.includes(q)
      );
    }
    return result.slice(0, 50); // Show max 50 results
  }, [locations, activeCourier, search]);

  if (error) {
    return (
      <div className="text-xs text-destructive p-2 rounded-lg bg-destructive/10 border border-destructive/20">
        Couldn't get the parcel locker list: {error}
      </div>
    );
  }

  if (selected && !showDropdown) {
    return (
      <div
        className="p-3 rounded-lg bg-primary/10 border border-primary/20 cursor-pointer hover:bg-primary/15 transition-colors"
        onClick={() => setShowDropdown(true)}
      >
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{selected.name}</p>
            <p className="text-xs text-foreground/60">{selected.address}, {selected.city}</p>
            <span className="text-[10px] text-primary/80 font-medium">{getCourierLabel(selected.courier_code)}</span>
          </div>
          <ChevronDown className="w-4 h-4 text-foreground/40 flex-shrink-0" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Courier filter tabs */}
      {!externalCourierFilter && couriers.length > 1 && (
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setInternalCourierFilter("all")}
            className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${
              activeCourier === "all" ? "bg-primary/20 text-primary border border-primary/30" : "bg-white/5 text-foreground/50 border border-white/10 hover:bg-white/10"
            }`}
          >
            All
          </button>
          {couriers.map(c => (
            <button
              key={c}
              onClick={() => setInternalCourierFilter(c)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                activeCourier === c ? "bg-primary/20 text-primary border border-primary/30" : "bg-white/5 text-foreground/50 border border-white/10 hover:bg-white/10"
              }`}
            >
              {getCourierLabel(c)}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground/30" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, address or city..."
          className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary/40 transition-colors"
        />
      </div>

      {/* List */}
      <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-white/10 bg-white/[0.02] p-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-foreground/40 text-center py-4">
            {search ? "Nothing found" : "The parcel locker list is empty"}
          </p>
        ) : (
          filtered.map((loc) => (
            <button
              key={loc.id}
              onClick={() => {
                onSelect(loc);
                setShowDropdown(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-start gap-2 ${
                selected?.id === loc.id
                  ? "bg-primary/15 border border-primary/30"
                  : "hover:bg-white/5 border border-transparent"
              }`}
            >
              <Package className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-foreground/40" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground leading-tight">{loc.name}</p>
                <p className="text-[10px] text-foreground/50">{loc.address}, {loc.city} {loc.postal_code}</p>
                <span className="text-[9px] text-foreground/30">{getCourierLabel(loc.courier_code)}</span>
              </div>
            </button>
          ))
        )}
        {filtered.length >= 50 && (
          <p className="text-[10px] text-foreground/30 text-center py-1">Showing 50 results. Refine your search.</p>
        )}
      </div>
    </div>
  );
};

export default ParcelLockerSelector;
